import { CustodianActivityStates } from '@generated/graphql';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { Box, Button } from 'hephaestus/web';
import type { ChangeEvent, SyntheticEvent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACTIVITIES_AGE_FILTER_DAYS,
  ACTIVITIES_AGE_FILTER_I18N,
  ACTIVITIES_ANY_FILTER,
} from '../utils/activities.config';
import type { ActivitiesFiltersCardProps } from './activitiesFiltersCard.types';

export const ActivitiesFiltersCard = ({
  state,
  onStateChange,
  transactionType,
  onTransactionTypeChange,
  transactionTypeOptions,
  agingFilter,
  onAgingFilterChange,
  search,
  onSearchChange,
  hasActiveFilters,
  onClearFilters,
  allVisibleExpanded,
  onToggleExpandAll,
  expandAllDisabled,
}: ActivitiesFiltersCardProps) => {
  const { t } = useTranslation(['reconciliation']);

  const typeOptions = useMemo(
    () => [ACTIVITIES_ANY_FILTER, ...transactionTypeOptions],
    [transactionTypeOptions],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Button
          size='small'
          variant='outlined'
          color='primary'
          disabled={expandAllDisabled}
          onClick={onToggleExpandAll}
          data-testid='activities-expand-all-toggle'
        >
          {allVisibleExpanded
            ? t('activitiesV3.filters.collapseAll')
            : t('activitiesV3.filters.expandAll')}
        </Button>
        {hasActiveFilters && (
          <Button
            size='small'
            variant='text'
            color='primary'
            onClick={onClearFilters}
            data-testid='activities-clear-filters'
          >
            {t('activitiesV3.filters.clearFilters')}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1.5,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          select
          fullWidth
          size='small'
          label={t('activitiesV3.filters.state')}
          value={state}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onStateChange(e.target.value)
          }
          testId='activities-filter-state'
        >
          <MenuItem value={CustodianActivityStates.Created}>
            {t('activitiesV3.filters.stateCreated')}
          </MenuItem>
          <MenuItem value={CustodianActivityStates.Reconciled}>
            {t('activitiesV3.filters.stateReconciledToday')}
          </MenuItem>
        </TextField>
        <Autocomplete
          fullWidth
          size='small'
          testId='activities-filter-type'
          label={t('activitiesV3.filters.type')}
          options={typeOptions}
          value={transactionType}
          getOptionLabel={(option: string) =>
            option === ACTIVITIES_ANY_FILTER
              ? t('activitiesV3.filters.any')
              : option
          }
          onChange={(_event: SyntheticEvent, next: string | null) =>
            onTransactionTypeChange(next ?? ACTIVITIES_ANY_FILTER)
          }
        />
        <TextField
          select
          fullWidth
          size='small'
          label={t('activitiesV3.filters.aging')}
          value={agingFilter}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onAgingFilterChange(e.target.value)
          }
          testId='activities-filter-aging'
        >
          <MenuItem value={ACTIVITIES_ANY_FILTER}>
            {t('activitiesV3.filters.any')}
          </MenuItem>
          {ACTIVITIES_AGE_FILTER_DAYS.map((days) => (
            <MenuItem key={days} value={String(days)}>
              {t(`activitiesV3.filters.${ACTIVITIES_AGE_FILTER_I18N[days]}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          size='small'
          leadingIcon='search'
          label={t('activitiesV3.filters.search')}
          placeholder={t('activitiesV3.filters.searchPlaceholder')}
          value={search}
          onChange={onSearchChange}
          testId='activities-filter-search'
        />
      </Box>
    </Box>
  );
};
